// src/components/advance/Advance.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./advance.css";
import { fetchAdvanceList, saveAdvanceTx } from "../../services/advance";
import { useCustomerRepo } from "../../repos/AppRepoProvider";

/* ---------------- helpers ---------------- */
function pad2(n) {
  const x = String(n);
  return x.length === 1 ? `0${x}` : x;
}
function todayDmy() {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}
function isoToDmy(iso) {
  const s = String(iso || "").trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}
function dmyToIso(dmy) {
  const s = String(dmy || "").trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${pad2(m[2])}-${pad2(m[1])}`;
}
function safeParseArray(v) {
  try {
    const s = typeof v === "string" ? v.trim() : v;
    const out = typeof s === "string" ? JSON.parse(s) : s;
    return Array.isArray(out) ? out : [];
  } catch {
    return [];
  }
}
function fmt2(x) {
  const n = Number(x || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}
function toNum(v) {
  const s = String(v ?? "").trim();
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/* ---------------- Portal Modal ---------------- */
function PortalModal({ children }) {
  return ReactDOM.createPortal(children, document.body);
}

/* ---------------- Customer Picker Modal ---------------- */
function CustomerPickerModal({ open, onClose, onPick, customers }) {
  const [q, setQ] = useState("");

  // Try to read customer list from repo in multiple possible shapes
  const list = useMemo(() => {
    if (!customers) return [];
    const src =
      customers.list ||
      customers.rows ||
      customers.data ||
      customers.all ||
      (typeof customers.getAll === "function" ? customers.getAll() : null) ||
      [];
    if (!Array.isArray(src)) return [];
    return src
      .map((c) => ({
        kno: String(c?.kno ?? c?.code ?? "").trim(),
        cname: String(c?.cname ?? c?.name ?? "").trim(),
        mobno: String(c?.mobno ?? c?.mobile ?? "").trim(),
      }))
      .filter((c) => c.kno && c.cname);
  }, [customers]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (c) =>
        String(c.kno).toLowerCase().includes(s) ||
        String(c.cname).toLowerCase().includes(s) ||
        String(c.mobno).toLowerCase().includes(s)
    );
  }, [list, q]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  if (!open) return null;

  return (
    <PortalModal>
      <div className="advBackdrop" onMouseDown={onClose}>
        <div className="advModal advPickModal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="advModalHead">
            <div className="advModalTitle">Select Farmer</div>
            <button type="button" className="advCloseBtn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="advPickTop">
            <div className="advPickCaption">Farmers</div>
            <div className="advSearchBox">
              <span className="advSearchLabel">Search</span>
              <input className="advInput" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" />
            </div>
          </div>

          <div className="advTableWrap" style={{ maxHeight: 420, overflow: "auto" }}>
            <table className="advTable" style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Code</th>
                  <th>Name</th>
                  <th style={{ width: 180 }}>Mobile</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="advMsg">
                      {list.length === 0 ? "Customer list not ready (repo has no list)." : "No farmers"}
                    </td>
                  </tr>
                ) : (
                  visible.map((c) => (
                    <tr
                      key={`${c.kno}-${c.cname}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => onPick(c)}
                      title="Click to select"
                    >
                      <td>{c.kno}</td>
                      <td>
                        <b>{c.cname}</b>
                      </td>
                      <td>{c.mobno || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="advFooterRow">
            <div style={{ color: "#666" }}>Showing {visible.length ? 1 : 0} to {visible.length} of {visible.length} entries</div>
            <div />
          </div>
        </div>
      </div>
    </PortalModal>
  );
}

/* ---------------- Add New Advance Modal ---------------- */
function AddAdvanceModal({ open, onClose, onSaved, mainuser }) {
  const customers = useCustomerRepo();

  const [kno, setKno] = useState("");
  const [cname, setCname] = useState("");
  const [mobno, setMobno] = useState("");

  const [dateDmy, setDateDmy] = useState(todayDmy());
  const [amt, setAmt] = useState("");
  const [intr, setIntr] = useState("");
  const [remark, setRemark] = useState("");

  const [saveContinue, setSaveContinue] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);

  // native date picker helper
  const dateIsoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // reset when opened
    setKno("");
    setCname("");
    setMobno("");
    setDateDmy(todayDmy());
    setAmt("");
    setIntr("");
    setRemark("");
    setSaveContinue(false);
    setSaving(false);
  }, [open]);

  // cname/mob lookup like Foods
  useEffect(() => {
    const kn = String(kno || "").trim();
    if (!kn || !customers?.ready) {
      setCname("");
      setMobno("");
      return;
    }
    setCname(customers.getNameByKno?.(kn) || "");
    try {
      setMobno(customers.getMobByKno?.(kn) || "");
    } catch {
      setMobno("");
    }
  }, [kno, customers]);

  function validate() {
    if (!String(kno).trim()) return "Farmer Code required";
    if (!String(dateDmy).trim()) return "Date required";
    if (!String(amt).trim() || !(Number(amt) > 0)) return "Amount required";
    if (!String(remark).trim()) return "Comments required";
    // validate dd/MM/yyyy format
    const iso = dmyToIso(dateDmy);
    if (!iso) return "Invalid date (use dd/MM/yyyy)";
    return "";
  }

  async function onSave() {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    const mobileuser = localStorage.getItem("mobileuser") || String(mainuser || "");

    setSaving(true);
    try {
      const payload = {
        mainuser: String(mainuser || ""),
        mobileuser: String(mobileuser || ""),
        date: String(dateDmy || ""), // dd/MM/yyyy
        kno: String(kno || ""),
        type: "Debit",
        amt: String(amt || ""),
        intr: String(intr || ""),
        remark: String(remark || ""),
        sredit: "-", // new mode
        flag: "new",
      };

      const resText = await saveAdvanceTx(payload);

      if (String(resText || "").toLowerCase().includes("saved")) {
        // ok
      } else if (String(resText || "").toLowerCase().includes("updated")) {
        // ok
      } else {
        // still allow, but show message
        // (some servers return different strings)
        console.log("save response:", resText);
      }

      await onSaved?.();

      if (saveContinue) {
        // keep date, clear fields like Android reset
        setKno("");
        setCname("");
        setMobno("");
        setAmt("");
        setIntr("");
        setRemark("");
      } else {
        onClose();
      }
    } catch (e) {
      alert(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function onReset() {
    setKno("");
    setCname("");
    setMobno("");
    setDateDmy(todayDmy());
    setAmt("");
    setIntr("");
    setRemark("");
    setSaveContinue(false);
  }

  if (!open) return null;

  return (
    <>
      <PortalModal>
        <div className="advBackdrop" onMouseDown={onClose}>
          <div className="advModal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="advModalHead advModalHeadGreen">
              <div className="advModalTitle">Add New Advance</div>
              <button type="button" className="advCloseBtn" onClick={onClose} aria-label="Close">
                ×
              </button>
            </div>

            <div className="advModalBody">
              <div className="advFormGrid">
                <div className="advLabel">
                  Farmer Code <span className="advReq">*</span>
                </div>
                <div className="advFieldRow">
                  <input className="advInput" value={kno} onChange={(e) => setKno(e.target.value)} />
                  <button type="button" className="advIconBtn" title="Search farmer" onClick={() => setPickerOpen(true)}>
                    🔍
                  </button>
                </div>

                <div />
                <div className="advSmallInfo">
                  {cname ? <b>{cname}</b> : "\u00A0"} {mobno ? ` • ${mobno}` : ""}
                </div>

                <div className="advLabel">
                  Date <span className="advReq">*</span>
                </div>
                <div className="advFieldRow">
                  <input
                    className="advInput"
                    value={dateDmy}
                    onChange={(e) => setDateDmy(e.target.value)}
                    placeholder="dd/MM/yyyy"
                    onFocus={() => {
                      // open native picker if available
                      if (dateIsoRef.current) dateIsoRef.current.showPicker?.();
                    }}
                  />
                  <button
                    type="button"
                    className="advIconBtn"
                    title="Pick date"
                    onClick={() => dateIsoRef.current?.showPicker?.()}
                  >
                    📅
                  </button>

                  {/* hidden native date input */}
                  <input
                    ref={dateIsoRef}
                    type="date"
                    className="advHiddenDate"
                    value={dmyToIso(dateDmy)}
                    onChange={(e) => setDateDmy(isoToDmy(e.target.value))}
                  />
                </div>

                <div className="advLabel">
                  Amount <span className="advReq">*</span>
                </div>
                <div>
                  <input className="advInput" value={amt} onChange={(e) => setAmt(e.target.value)} />
                </div>

                <div className="advLabel">Interest Rate</div>
                <div>
                  <input className="advInput" value={intr} onChange={(e) => setIntr(e.target.value)} />
                  <div className="advHint">Interest per Month</div>
                </div>

                <div className="advLabel">
                  Comments <span className="advReq">*</span>
                </div>
                <div>
                  <textarea className="advTextarea" value={remark} onChange={(e) => setRemark(e.target.value)} />
                </div>

                <div />
                <div className="advCheckboxRow">
                  <label>
                    <input type="checkbox" checked={saveContinue} onChange={(e) => setSaveContinue(e.target.checked)} />{" "}
                    Save and continue
                  </label>
                </div>
              </div>

              <div className="advActions">
                <button type="button" className="advBtnGhost" onClick={onReset} disabled={saving}>
                  Reset
                </button>

                <button type="button" className="advBtnPrimary" onClick={onSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PortalModal>

      <CustomerPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        customers={customers}
        onPick={(c) => {
          setKno(c.kno);
          setCname(c.cname);
          setMobno(c.mobno);
          setPickerOpen(false);
        }}
      />
    </>
  );
}

/* ---------------- Main Advance page ---------------- */
export default function Advance({ mainuser }) {
  const [includePaid, setIncludePaid] = useState(false);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  const [addOpen, setAddOpen] = useState(false);

  async function loadAdvance() {
    if (!mainuser) return;

    setLoading(true);
    setErr("");
    try {
      const text = await fetchAdvanceList(mainuser);
      const arr = safeParseArray(text);

      const seen = new Set();
      const mapped = [];

      for (const o of arr) {
        const kno = parseInt(String(o?.kno ?? "0").trim(), 10) || 0;
        const cname = String(o?.cname ?? "").trim();
        if (kno <= 0) continue;
        if (!cname || cname === "0") continue;

        const key = `${kno}|${cname}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const balance = toNum(o?.current_balance ?? o?.balance ?? 0);

        // if server later adds amount/paid, we support it
        const amount = toNum(o?.amount ?? o?.amt ?? o?.total_amount ?? 0);
        const paid = toNum(o?.paid ?? o?.paid_amount ?? 0);

        mapped.push({
          code: String(kno),
          name: cname,
          amount,
          paid,
          balance,
        });
      }

      setRows(mapped);
    } catch (e) {
      console.error("advance load error", e);
      setErr(e?.message || "Network Error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!mainuser) return;
    loadAdvance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainuser]);

  const visibleRows = useMemo(() => {
    let out = rows;

    // Include Paid => include zero balances
    if (!includePaid) out = out.filter((r) => Number(r.balance || 0) !== 0);

    const q = search.trim().toLowerCase();
    if (!q) return out;

    return out.filter(
      (r) =>
        String(r.code || "").toLowerCase().includes(q) ||
        String(r.name || "").toLowerCase().includes(q)
    );
  }, [rows, includePaid, search]);

  const totals = useMemo(() => {
    let amount = 0;
    let paid = 0;
    let balance = 0;
    for (const r of visibleRows) {
      amount += Number(r.amount || 0) || 0;
      paid += Number(r.paid || 0) || 0;
      balance += Number(r.balance || 0) || 0;
    }
    return { amount, paid, balance };
  }, [visibleRows]);

  const totalEntries = visibleRows.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));
  const safePage = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return visibleRows.slice(start, start + pageSize);
  }, [visibleRows, safePage, pageSize]);

  const showingFrom = totalEntries === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = Math.min(safePage * pageSize, totalEntries);

  useEffect(() => {
    setPage(1);
  }, [includePaid, search, pageSize]);

  return (
    <div className="advPage">
      <div className="advTopBar">
        <div className="advTitleLeft">
          <span className="advMiniIcon" aria-hidden="true">
            💳
          </span>
          <span className="advPageTitle">Advance</span>
        </div>

        <button className="advAddBtn" type="button" onClick={() => setAddOpen(true)}>
          + Add New Advance
        </button>
      </div>

      <label className="advIncludePaid">
        <input type="checkbox" checked={includePaid} onChange={(e) => setIncludePaid(e.target.checked)} />
        <span>Include Paid</span>
      </label>

      <div className="advCard">
        <div className="advCardHeader">
          <div className="advCardTitle">Advance</div>

          <div className="advTools">
            <button className="advPrintBtn" type="button">
              🖨 Print
            </button>

            <div className="advSearchBox">
              <span className="advSearchLabel">Search</span>
              <input className="advSearchInput" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
            </div>
          </div>
        </div>

        {err ? <div className="advMsg" style={{ color: "#b42318" }}>{err}</div> : null}

        <div className="advTableWrap">
          <table className="advTable">
            <thead>
              <tr>
                <th style={{ width: 90 }}>SrNo</th>
                <th>Code</th>
                <th>Name</th>
                <th className="num">Amount</th>
                <th className="num">Paid</th>
                <th className="num">Balance</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="advMsg">
                    Loading...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={6} className="advMsg">
                    No rows
                  </td>
                </tr>
              ) : (
                <>
                  {paged.map((r, idx) => (
                    <tr key={`${r.code}-${idx}`}>
                      <td>{(safePage - 1) * pageSize + idx + 1}</td>
                      <td>{r.code}</td>
                      <td className="nameStrong">{r.name}</td>
                      <td className="num">{fmt2(r.amount)}</td>
                      <td className="num">{fmt2(r.paid)}</td>
                      <td className={`num ${Number(r.balance) > 0 ? "balRed" : ""}`}>{fmt2(r.balance)}</td>
                    </tr>
                  ))}

                  <tr className="advTotalRow">
                    <td>-</td>
                    <td className="totalLabel">Total</td>
                    <td>-</td>
                    <td className="num">{fmt2(totals.amount)}</td>
                    <td className="num">{fmt2(totals.paid)}</td>
                    <td className="num">{fmt2(totals.balance)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="advFooter">
          <div className="advShowing">
            Showing {showingFrom} to {showingTo} of {totalEntries} entries
          </div>

          <div className="advPager">
            <button type="button" className="advPgBtn" onClick={() => setPage(1)} disabled={safePage === 1}>
              «
            </button>
            <button type="button" className="advPgBtn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>
              ‹
            </button>

            <button type="button" className="advPgNum active">{safePage}</button>

            <button type="button" className="advPgBtn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>
              ›
            </button>
            <button type="button" className="advPgBtn" onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>
              »
            </button>

            <select className="advPageSize" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      <AddAdvanceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={loadAdvance}
        mainuser={mainuser}
      />
    </div>
  );
}
